import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterGroup from '@/components/feature/FilterGroup';
import Pagination from '@/components/feature/Pagination';
import SearchBar from '@/components/feature/SearchBar';
import WorkCard from '@/components/feature/WorkCard';
import { works } from '@/mocks/works';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;

const CATEGORY_OPTIONS = [
  { value: 'all', label: '全部类别' },
  { value: 'movie', label: '电影' },
  { value: 'tv', label: '电视剧' },
];

const SORT_OPTIONS = [
  { value: 'latest', label: '最新上映' },
  { value: 'rating', label: '评分最高' },
  { value: 'popular', label: '最受欢迎' },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';

  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);

  const trimmed = query.trim();

  useSeo({
    title: trimmed ? `“${trimmed}”的搜索结果 - 美丽小蜜桃3影视大全` : '搜索影视资源 - 美丽小蜜桃3影视大全',
    description: trimmed
      ? `美丽小蜜桃3影视大全为你找到与“${trimmed}”相关的电影和电视剧资源，包含剧情介绍、演员阵容与上映信息。`
      : '在美丽小蜜桃3影视大全中搜索电影、电视剧的名称与主演，快速定位你想了解的影视作品。',
    keywords: `美丽小蜜桃3,影视搜索,${trimmed || '影视大全'},电影搜索,电视剧搜索`,
  });

  useEffect(() => {
    setPage(1);
  }, [query, category, sort]);

  const results = useMemo(() => {
    if (!trimmed) return [];
    return works
      .filter((work) => {
        const lower = trimmed.toLowerCase();
        return (
          work.title.toLowerCase().includes(lower) ||
          work.actors.some((actor) => actor.toLowerCase().includes(lower)) ||
          work.genres.some((genre) => genre.toLowerCase().includes(lower)) ||
          work.director.toLowerCase().includes(lower)
        );
      })
      .filter((work) => (category === 'all' ? true : work.type === category))
      .sort((a, b) => {
        if (sort === 'rating') return b.rating - a.rating;
        if (sort === 'popular') return b.popularity - a.popularity;
        return b.year - a.year || b.popularity - a.popularity;
      });
  }, [trimmed, category, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearch = (value: string) => {
    setSearchParams({ q: value });
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="container-page py-6 md:py-9">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />
          <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">
            {trimmed ? `“${trimmed}”的搜索结果` : '搜索影视资源'}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 md:text-base">
            {trimmed
              ? `正在为你查找与“${trimmed}”相关的电影、电视剧、主演与类型。`
              : '输入影视名称或主演关键词，快速找到你想了解的影视作品。'}
          </p>
          <div className="mt-6 max-w-2xl">
            <SearchBar initialValue={trimmed} size="lg" onSubmit={handleSearch} showSuggestions />
          </div>
        </div>
      </section>

      {trimmed && (
        <section className="container-page pt-6 md:pt-8">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <FilterGroup label="类别" options={CATEGORY_OPTIONS} value={category} onChange={setCategory} />
            <FilterGroup label="排序" options={SORT_OPTIONS} value={sort} onChange={setSort} />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-semibold text-primary-600">{results.length}</span> 条与“{trimmed}”相关的结果
            </p>
            {(category !== 'all' || sort !== 'latest') && (
              <button
                type="button"
                onClick={() => {
                  setCategory('all');
                  setSort('latest');
                }}
                className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            )}
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-5 xl:grid-cols-5">
                {pageItems.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
              <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="mt-16 flex flex-col items-center justify-center py-10 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-search-line text-3xl" />
              </span>
              <p className="mt-4 text-base font-medium text-foreground-800">未找到相关影视资源</p>
              <p className="mt-1 max-w-md text-sm text-foreground-500">
                没有找到与“{trimmed}”匹配的影视作品，换个关键词试试，或返回首页浏览分类内容。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Link
                  to="/"
                  className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  返回首页
                </Link>
                <Link
                  to="/category/movie"
                  className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  浏览电影大全
                </Link>
              </div>
            </div>
          )}
        </section>
      )}

      {!trimmed && (
        <section className="container-page py-12 md:py-16">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center md:p-10">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-lightbulb-line text-2xl" />
            </span>
            <h2 className="mt-4 text-lg font-bold text-foreground-950 md:text-xl">还没有输入搜索关键词</h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-foreground-600">
              你可以搜索影视名称（如“星河”）、主演（如“顾清越”）或者类型（如“悬疑”“科幻”）来查找作品。
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {['悬疑', '科幻', '古装', '星河追缉', '长安夜雨'].map((keyword) => (
                <button
                  key={keyword}
                  type="button"
                  onClick={() => setSearchParams({ q: keyword })}
                  className="cursor-pointer rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                >
                  {keyword}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
}